/**
 * Feed gateway client — the tab's ONE feed socket (relay/src/feedgw).
 *
 * The tab declares a list of SCOPES and the gateway sends exactly the rows
 * those scopes match, plus `remove` when a scope narrows. Frames arrive in
 * hydra's own shapes, so everything downstream of the callbacks (marketStore
 * normalization, pairing, NO→YES inversion) is untouched.
 *
 *   board    headline markets for the listed wire sports — never props
 *   game     every class for one game + its odds + its game state
 *   books    order-book depth for the listed tickers
 *   tickers  rows for held positions / resting orders
 *
 * Deliberately a plain WebSocket, no SharedWorker: the worker existed to share
 * ~52 upstream sockets across tabs and to parse a firehose off the main
 * thread. One scoped socket per tab needs neither.
 */
import type { HydraGameStateMsg, HydraMarket, HydraOpOddsMsg, HydraOrderbook } from './HydraClient';
import type { SourceObservation } from '../lib/gameState/sourceEvidence';
import { captureSourceAge, type SourceAge } from '../lib/sourceAge';

export interface GatewayGameScope {
  /** Wire labels that may carry the game's rows (base sport + split labels). */
  sports: string[];
  /** Candidate wire keys — every spelling the tab can compose. The gateway
   *  matches them loosely (date + team pair), so an extra candidate is free
   *  and a missing one is covered by the others. */
  keys: string[];
}

export interface GatewayScopes {
  boardSports: string[];
  /** Which board tab is open (`main` / `lines` / `futures`); null = every kind. */
  boardKinds: string[] | null;
  games: GatewayGameScope[];
  /** Per-delta books (the ticket, ladders). */
  bookTickers: string[];
  /** Board-cell books, served at the gateway's coalesced cadence. */
  boardBookTickers: string[];
  heldTickers: string[];
}

export interface GatewayAlive {
  markets: boolean;
  oddspapiAgeMs: number | null;
  gamestateAgeMs: number | null;
  /** The gateway's send stamp (epoch ms) — the health meter's queue-lag input. */
  serverTs: number | null;
}

export interface GatewayCallbacks {
  /** Same contract as HydraCallbacks: `age` is source-qualified age evidence
   *  captured from the frame the gateway forwarded verbatim. */
  onMarket(market: HydraMarket, age?: SourceAge): void;
  onOrderbook(ticker: string, book: HydraOrderbook, age?: SourceAge, observation?: SourceObservation): void;
  onOpOdds(msg: HydraOpOddsMsg): void;
  onGameState(msg: HydraGameStateMsg): void;
  onActiveSports(sports: string[]): void;
  /** Rows the tab's scopes no longer cover, or that the feed withdrew. */
  onRemove(tickers: string[]): void;
  /** Games whose odds the tab's scopes no longer cover (V2 keys). */
  onRemoveGames(keys: string[]): void;
  onAlive(alive: GatewayAlive): void;
  /** A pong to OUR ping: the gateway's stamp and when the ping left (epoch ms). */
  onPong?(serverTs: number, sentAtMs: number): void;
  onStatus(connected: boolean): void;
  onKeepalive(): void;
  /** The gateway could not be reached for `giveUpAfterMs` — the caller falls
   *  back to the fleet. Fired once. */
  onGiveUp(): void;
}

export interface GatewayClientOptions {
  url: string;
  getToken: () => string | null;
  /** Coalesce scope changes (the needs registries publish with zero grace). */
  scopeDebounceMs?: number;
  giveUpAfterMs?: number;
  livenessTimeoutMs?: number;
}

const PING_INTERVAL_MS = 10_000;
const BACKOFF_BASE_MS = 500;
const BACKOFF_MAX_MS = 10_000;

/** Server-side `MAX_TICKERS_PER_SCOPE` (relay/src/feedgw/scopes.ts). */
export const MAX_TICKERS_PER_SCOPE = 2_000;
function chunks(list: string[]): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < list.length; i += MAX_TICKERS_PER_SCOPE) out.push(list.slice(i, i + MAX_TICKERS_PER_SCOPE));
  return out;
}

const EMPTY: GatewayScopes = { boardSports: [], boardKinds: null, games: [], bookTickers: [], boardBookTickers: [], heldTickers: [] };
/** What the tab asks for board-cell books. The server serves any cadence > 0
 *  from its one coalesced tier (`FEEDGW_BOARD_BOOK_CADENCE_MS`), so this is a
 *  request, not a contract — 250 ms matches the board's markets cadence. */
export const BOARD_BOOK_CADENCE_MS = 250;

export class GatewayClient {
  private ws: WebSocket | null = null;
  private stopped = true;
  private _connected = false;
  private _lastMessageMs = 0;
  private _connectedSinceMs = 0;
  private backoffMs = BACKOFF_BASE_MS;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pingSentAtMs = 0;
  private scopeTimer: ReturnType<typeof setTimeout> | null = null;
  private scopes: GatewayScopes = EMPTY;
  private rev = 0;
  private sentKey = '';
  private downSinceMs = 0;
  private gaveUp = false;
  /** Tickers this socket has delivered and not removed — reconciled after a
   *  reconnect, when rows withdrawn while we were away never get a `remove`. */
  private known = new Set<string>();
  private resyncSeen: Set<string> | null = null;
  /** Source-age evidence is scoped to ONE connection: a reconnect starts a new
   *  generation so replayed frames can never read as younger than they are. */
  private ageGeneration = 'gateway-0';
  private connects = 0;
  framesReceived = 0;
  bytesReceived = 0;

  private readonly scopeDebounceMs: number;
  private readonly giveUpAfterMs: number;
  private readonly livenessTimeoutMs: number;

  constructor(private readonly cb: GatewayCallbacks, private readonly opts: GatewayClientOptions) {
    this.scopeDebounceMs = opts.scopeDebounceMs ?? 40;
    this.giveUpAfterMs = opts.giveUpAfterMs ?? 10_000;
    this.livenessTimeoutMs = opts.livenessTimeoutMs ?? 20_000;
  }

  get connected(): boolean { return this._connected; }
  get lastMessageMs(): number { return this._lastMessageMs; }
  get connectedSinceMs(): number { return this._connectedSinceMs; }
  get transport(): 'gateway' { return 'gateway'; }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.downSinceMs = Date.now();
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.scopeTimer) clearTimeout(this.scopeTimer);
    this.reconnectTimer = this.pingTimer = this.scopeTimer = null;
    const ws = this.ws;
    this.ws = null;
    try { ws?.close(); } catch { /* closing */ }
    if (this._connected) { this._connected = false; this.cb.onStatus(false); }
  }

  /** Replace one or more scope groups. Sends are debounced and de-duplicated. */
  setScopes(patch: Partial<GatewayScopes>): void {
    this.scopes = { ...this.scopes, ...patch };
    if (this.scopeTimer) return;
    this.scopeTimer = setTimeout(() => { this.scopeTimer = null; this.sendScopes(); }, this.scopeDebounceMs);
  }

  /** `FeedTransport` — marketStore routes every view's book needs here. */
  setOrderbookNeeds(tickers: string[], coalescible: string[] = []): void {
    const slow = new Set(coalescible);
    this.setScopes({ bookTickers: tickers.filter((t) => !slow.has(t)), boardBookTickers: tickers.filter((t) => slow.has(t)) });
  }

  /** `FeedTransport` — re-request the full scoped snapshot. The store asks on
   *  its staleness heuristics (activity fraction on a toolbar click, a tab
   *  returning from the background, the 60 s watchdog). On this transport a
   *  quiet ROW is not a quiet PIPE: the gateway is a stateful proxy that holds
   *  Polymarket alt lines it deliberately does not stream, and its own
   *  liveness gate (`livenessTimeoutMs`) already re-dials a silent socket. So
   *  the request is honoured only when the socket really is silent — a
   *  reconnect otherwise re-sends the whole board (26 MB on a CFB lines board,
   *  measured 2026-09-23) for nothing. */
  requestResnapshot(): void {
    if (this._connected && this._lastMessageMs > 0 && Date.now() - this._lastMessageMs < this.livenessTimeoutMs) return;
    this.forceReconnect();
  }

  /** Drop an ESTABLISHED socket so it re-dials and re-snapshots. A socket that
   *  is still connecting is left alone — it will authenticate with the current
   *  token and snapshot on its own (closing it mid-handshake only churned it:
   *  the boot-time token refresh did exactly that, 2026-09-21). */
  forceReconnect(): void {
    if (this.stopped || !this._connected) return;
    try { this.ws?.close(); } catch { /* closing */ }
  }

  private wireScopes(): unknown[] {
    const s = this.scopes;
    const out: unknown[] = [];
    if (s.boardSports.length > 0) {
      out.push({ kind: 'board', sports: [...s.boardSports].sort(), ...(s.boardKinds ? { kinds: [...s.boardKinds].sort() } : {}) });
    }
    for (const g of s.games) if (g.keys.length > 0 && g.sports.length > 0) out.push({ kind: 'game', sports: [...g.sports].sort(), keys: [...g.keys].sort() });
    // The gateway caps ONE scope at MAX_TICKERS_PER_SCOPE and rejects the whole
    // message over it (the tab would keep its stale scopes). Long lists — a
    // viewport's rows plus held positions plus a tracker's tickers — are sent
    // as several scopes of the same kind; the server ORs them.
    for (const chunk of chunks([...s.bookTickers].sort())) out.push({ kind: 'books', tickers: chunk });
    for (const chunk of chunks([...s.boardBookTickers].sort())) out.push({ kind: 'books', tickers: chunk, cadenceMs: BOARD_BOOK_CADENCE_MS });
    for (const chunk of chunks([...s.heldTickers].sort())) out.push({ kind: 'tickers', tickers: chunk });
    return out;
  }

  private sendScopes(force = false): void {
    if (!this._connected || !this.ws) return;
    const scopes = this.wireScopes();
    const key = JSON.stringify(scopes);
    if (!force && key === this.sentKey) return;
    this.sentKey = key;
    this.rev += 1;
    this.ws.send(JSON.stringify({ op: 'scopes', rev: this.rev, scopes }));
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer) return;
    if (!this.gaveUp && this.downSinceMs > 0 && Date.now() - this.downSinceMs >= this.giveUpAfterMs) {
      this.gaveUp = true;
      this.cb.onGiveUp();
      return;
    }
    const delay = Math.random() * this.backoffMs;
    this.backoffMs = Math.min(this.backoffMs * 2, BACKOFF_MAX_MS);
    this.reconnectTimer = setTimeout(() => { this.reconnectTimer = null; this.connect(); }, delay);
  }

  private connect(): void {
    if (this.stopped) return;
    const token = this.opts.getToken();
    if (!token) { this.scheduleReconnect(); return; } // not signed in yet
    let ws: WebSocket;
    try { ws = new WebSocket(this.opts.url); } catch { this.scheduleReconnect(); return; }
    this.ws = ws;
    ws.onopen = () => { ws.send(JSON.stringify({ op: 'hello', token })); };
    ws.onmessage = (ev) => {
      if (this.ws !== ws || typeof ev.data !== 'string') return;
      this._lastMessageMs = Date.now();
      this.framesReceived += 1;
      this.bytesReceived += ev.data.length;
      let msg: Record<string, unknown>;
      try { msg = JSON.parse(ev.data) as Record<string, unknown>; } catch { return; }
      this.dispatch(msg);
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      if (this._connected) {
        this._connected = false;
        this._connectedSinceMs = 0;
        this.downSinceMs = Date.now();
        this.cb.onStatus(false);
      }
      this.scheduleReconnect();
    };
    ws.onerror = () => { /* close follows */ };
  }

  private dispatch(msg: Record<string, unknown>): void {
    switch (msg.type) {
      case 'market': {
        const market = msg.market as HydraMarket | undefined;
        if (!market || typeof market.ticker !== 'string') return;
        this.known.add(market.ticker);
        this.resyncSeen?.add(market.ticker);
        this.cb.onMarket(market, captureSourceAge(msg, this.ageGeneration));
        return;
      }
      case 'orderbook': {
        if (typeof msg.ticker !== 'string') return;
        const o = msg as { lane?: unknown; sourceEpoch?: unknown; sourceGen?: unknown; observedAtMs?: unknown; snapshotMs?: unknown; pendingResync?: unknown };
        this.cb.onOrderbook(msg.ticker, msg.orderbook as HydraOrderbook, captureSourceAge(msg, this.ageGeneration), {
          lane: o.lane, sourceEpoch: o.sourceEpoch, sourceGen: o.sourceGen,
          observedAtMs: o.observedAtMs, snapshotMs: o.snapshotMs, pendingResync: o.pendingResync,
        } as SourceObservation);
        return;
      }
      case 'opOdds':
        this.cb.onOpOdds(msg as unknown as HydraOpOddsMsg);
        return;
      case 'gameState':
        this.cb.onGameState(msg as unknown as HydraGameStateMsg);
        return;
      case 'remove': {
        const tickers = Array.isArray(msg.tickers) ? msg.tickers.filter((t): t is string => typeof t === 'string') : [];
        for (const t of tickers) this.known.delete(t);
        if (tickers.length > 0) this.cb.onRemove(tickers);
        return;
      }
      case 'removeGames': {
        const keys = Array.isArray(msg.keys) ? msg.keys.filter((k): k is string => typeof k === 'string') : [];
        if (keys.length > 0) this.cb.onRemoveGames(keys);
        return;
      }
      case 'activeSports':
        this.cb.onActiveSports(Array.isArray(msg.sports) ? msg.sports.filter((s): s is string => typeof s === 'string') : []);
        return;
      case 'alive':
        this.cb.onKeepalive();
        this.cb.onAlive({
          markets: msg.markets !== false,
          oddspapiAgeMs: typeof msg.oddspapiAgeMs === 'number' ? msg.oddspapiAgeMs : null,
          gamestateAgeMs: typeof msg.gamestateAgeMs === 'number' ? msg.gamestateAgeMs : null,
          serverTs: typeof msg.serverTs === 'number' ? msg.serverTs : null,
        });
        return;
      case 'pong':
        this.cb.onKeepalive();
        if (typeof msg.serverTs === 'number' && this.pingSentAtMs > 0) this.cb.onPong?.(msg.serverTs, this.pingSentAtMs);
        return;
      case 'helloAck':
        this.onHello();
        return;
      case 'gwSnapshotEnd': {
        // First snapshot after a (re)connect: anything we held that the
        // gateway did not re-send was withdrawn while we were away.
        const seen = this.resyncSeen;
        if (!seen || msg.rev !== this.rev) return;
        this.resyncSeen = null;
        const gone = [...this.known].filter((t) => !seen.has(t));
        for (const t of gone) this.known.delete(t);
        if (gone.length > 0) this.cb.onRemove(gone);
        return;
      }
      case 'scopeAck':
        if (typeof msg.error === 'string') console.error(`[gateway] scopes rejected: ${msg.error}`);
        return;
      default:
        return; // forward-compatible
    }
  }

  private onHello(): void {
    this._connected = true;
    this._connectedSinceMs = Date.now();
    this.backoffMs = BACKOFF_BASE_MS;
    this.downSinceMs = 0;
    this.resyncSeen = new Set();
    this.connects += 1;
    this.ageGeneration = `gateway-${this.connects}`;
    this.cb.onStatus(true);
    this.sendScopes(true);
    const ws = this.ws;
    this.pingTimer = setInterval(() => {
      if (this.ws !== ws || !ws) return;
      if (Date.now() - this._lastMessageMs > this.livenessTimeoutMs) { try { ws.close(); } catch { /* closing */ } return; }
      this.pingSentAtMs = Date.now();
      try { ws.send('{"op":"ping"}'); } catch { /* closing */ }
    }, PING_INTERVAL_MS);
  }
}
